/**
 * SparePartsView - the Spare Parts tab (10/8/26, Shawn's v1 list): the parts
 * catalog with FANUC's STANDARD and GLOBAL prices and how stale they are,
 * robots by F-number with their recommended spare parts, and the stock we
 * own. The 'spare-parts' area (src/data/roles.js): View reads, Edit changes.
 *
 * The parts live in components/spareParts/: CatalogPanel, RobotsPanel,
 * InventoryPanel and PartDrawer (one part, opened from any of them).
 * Deep links: #/spare-parts?part=<id or part #>, ?view=robots&robot=<id>,
 * ?view=inventory - the Activity log opens records this way.
 */
import { useCallback, useEffect, useState } from 'react';
import { canEdit, canSeeTab } from '../data/roles';
import { useMyAccess } from '../hooks/useMyAccess';
import { parseHashFilters } from '../utils/hashParams';
import { SegmentedControl } from '../components/ui/SegmentedControl';
import CatalogPanel from '../components/spareParts/CatalogPanel';
import RobotsPanel from '../components/spareParts/RobotsPanel';
import InventoryPanel from '../components/spareParts/InventoryPanel';
import PartDrawer from '../components/spareParts/PartDrawer';

const VIEWS = [
  { value: 'catalog', label: 'Catalog' },
  { value: 'robots', label: 'Robots (F-numbers)' },
  { value: 'inventory', label: 'Inventory' },
];

/** The tab's place from the URL: { view, robot, part }. */
function fromHash() {
  const h = parseHashFilters();
  return {
    view: VIEWS.some((v) => v.value === h.view) ? h.view : 'catalog',
    robot: h.robot || null,
    part: h.part || null,
  };
}

export default function SparePartsView() {
  const access = useMyAccess();
  const editable = canEdit(access, 'spare-parts');
  const [place, setPlace] = useState(fromHash);
  // part: an id or part number; 'new' = the new-part form; null = closed
  const [part, setPart] = useState(() => fromHash().part);
  // this tab's own saves in the drawer: the live stream does not echo them
  // back to the tab that made them, so the lists behind it re-read on this
  const [tick, setTick] = useState(0);
  const changed = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    const onHash = () => {
      if (!/^#\/spare-parts\b/.test(window.location.hash)) return;
      const h = fromHash();
      setPlace(h);
      setPart(h.part);
    };
    window.addEventListener('hashchange', onHash);
    window.addEventListener('popstate', onHash);
    return () => {
      window.removeEventListener('hashchange', onHash);
      window.removeEventListener('popstate', onHash);
    };
  }, []);

  // keep the URL on the place shown, so a link or a reload comes back to it
  const go = useCallback((next) => {
    setPlace((cur) => {
      const p = { ...cur, ...next };
      const usp = new URLSearchParams();
      if (p.view !== 'catalog') usp.set('view', p.view);
      if (p.view === 'robots' && p.robot) usp.set('robot', p.robot);
      const qs = usp.toString();
      const hash = `#/spare-parts${qs ? `?${qs}` : ''}`;
      if (window.location.hash !== hash)
        window.history.replaceState(window.history.state, '', `${window.location.pathname}${window.location.search}${hash}`);
      return p;
    });
  }, []);
  const openRobot = useCallback(
    (id) => {
      setPart(null);
      go({ view: 'robots', robot: id ? String(id) : null });
    },
    [go]
  );

  if (!canSeeTab(access, 'spare-parts')) return null;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
        <SegmentedControl options={VIEWS} value={place.view} onChange={(v) => go({ view: v })} />
        <span style={{ fontSize: 11, color: '#6B7280' }}>
          {editable
            ? 'FANUC STANDARD (list) and GLOBAL prices, robots by F-number, and the stock we own.'
            : 'View only - changing parts needs Spare Parts Edit.'}
        </span>
      </div>
      {place.view === 'catalog' && <CatalogPanel tick={tick} editable={editable} onOpenPart={setPart} onNewPart={() => setPart('new')} />}
      {place.view === 'robots' && (
        <RobotsPanel
          tick={tick}
          editable={editable}
          robotId={place.robot}
          onPickRobot={(id) => go({ robot: id ? String(id) : null })}
          onOpenPart={setPart}
        />
      )}
      {place.view === 'inventory' && <InventoryPanel tick={tick} editable={editable} onOpenPart={setPart} />}
      {part && (
        <PartDrawer
          key={part}
          partId={part === 'new' ? null : part}
          editable={editable}
          onClose={() => setPart(null)}
          onOpened={(id) => {
            changed();
            setPart(String(id));
          }}
          onOpenRobot={openRobot}
          onChanged={changed}
        />
      )}
    </div>
  );
}
